import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import Select from 'components/Inputs/Select'
import AverageTickets from './AverageTickets'
import SchedulesByStatus from './SchedulesByStatus'
import CustomersByMonth from './CustomersByMonth'
import ReturnRatio from './ReturnRatio'
import CustomersByGender from './CustomersByGender'
import TopCustomers from './TopCustomers'
import TopServices from './TopServices'
import SoldItems from './SoldItems'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  DashboardContainer,
  LoadingContainer,
  FilterContainer,
  InfoText
} from '../styles'

export default function Sales ({ month, year }) {
  const selectedDate = DateTime.local (year.value, month.value).setLocale ('pt-BR')
  const formattedDate = selectedDate.toFormat ('MMM yyyy')
  const [loading, setLoading] = useState (true)
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const typeFilterOptions = Dashboard.TYPE_FILTERS
  const [periodFilter, setPeriodFilter] = useState (periodFilterOptions[0])
  const [typeFilter, setTypeFilter] = useState (typeFilterOptions[0])
  const [averageTickets, setAverageTickets] = useState (null)
  const [schedulesByStatus, setSchedulesByStatus] = useState (null)
  const [customersByGender, setCustomersByGender] = useState (null)
  const [customersByMonth, setCustomersByMonth] = useState (null)
  const [soldItemsByMonth, setSoldItemsByMonth] = useState (null)
  const [returnRatio, setReturnRatio] = useState (null)
  const [topCustomers, setTopCustomers] = useState (null)
  const [topServices, setTopServices] = useState (null)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [month, year, periodFilter, typeFilter])

  const getData = async () => {
    try {
      setLoading (true)

      const selectedDate = DateTime.local (year.value, month.value)
      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()
      const filters = `start_date=${startDate}&end_date=${endDate}&type=${typeFilter.value}`

      const [
        { data: avgServiceTickets },
        { data: avgCustomerTickets },
        { data: schedulesByStatus },
        { data: customersByMonth },
        { data: customersByGender },
        { data: topCustomers },
        { data: topServices },
        { data: soldItemsByMonth }
      ] = await Promise.all ([
        cutileiApi.get (`/businesses/${businessId}/avg_service_ticket?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/avg_customer_ticket?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/schedules_by_status?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/customers_by_month?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/customers_by_gender?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/top_customers?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/top_services?${filters}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/sold_items?${filters}`, requestConfig)
      ])

      const statusValues = Object.values (schedulesByStatus)

      setAverageTickets ([
        ['Tickets médios', 'Por serviço', 'Por cliente'],
        ...Object.keys (avgServiceTickets).map (month => ([
          month,
          avgServiceTickets[month],
          avgCustomerTickets[month]
        ]))
      ])

      setSchedulesByStatus ([
        ['Status', 'Qauntidade'],
        ['Realizados', Object.values (statusValues)[statusValues.length-1].completedCount],
        ['Cliente faltou', Object.values (statusValues)[statusValues.length-1].missedCount],
        ['Cancelados', Object.values (statusValues)[statusValues.length-1].canceledCount]
      ])

      setTopCustomers (topCustomers)
      setTopServices (topServices)

      setCustomersByGender ([
        ['Gênero', 'Clientes'],
        ['Masculino', customersByGender[formattedDate]?.['Masculino'] ?? 0],
        ['Feminino', customersByGender[formattedDate]?.['Feminino'] ?? 0],
        ['Não informado', customersByGender[formattedDate]?.['Não informar'] ?? 0]
      ])

      setCustomersByMonth ([
        ['Mês', 'Demais clientes', 'Retornos do mês anterior', 'Novos clientes'],
        ...Object.entries (customersByMonth).map (([month, values]) => [
          month,
          Number (values.attended) || 0,
          Number (values.returned) || 0,
          Number (values.new) || 0
        ])
      ])

      setReturnRatio ([
        ['Mês', 'Taxa de retorno (%)'],
        ...Object.entries (customersByMonth).map (([month, values]) => [
          month,
          Number (values.returnRatio) || 0
        ])
      ])

      setSoldItemsByMonth ([
        ['Item', 'Qauntidade'],
        ['Serviços', soldItemsByMonth[formattedDate]?.services.total],
        ['Pacotes', soldItemsByMonth[formattedDate]?.bundles.total],
        ['Produtos', soldItemsByMonth[formattedDate]?.products.total]
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <>
      <FilterContainer style={{marginTop: 0, marginBottom: 20}}>
        <Select
          placeholder='Período...'
          value={periodFilter}
          options={periodFilterOptions}
          onChange={(_, option) => setPeriodFilter (option)}
        />
        <Select
          placeholder='Tipo de agendamento...'
          value={typeFilter}
          options={typeFilterOptions}
          onChange={(_, option) => setTypeFilter (option)}
        />
      </FilterContainer>
      <DashboardContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráficos...</InfoText>
          </LoadingContainer>
        ) : (
          <>
            <CustomersByMonth data={customersByMonth}/>
            <ReturnRatio data={returnRatio}/>
            <AverageTickets data={averageTickets}/>
            <TopServices data={topServices} month={month.value} year={year.value}/>
            <SoldItems data={soldItemsByMonth} month={month.value} year={year.value}/>
            <SchedulesByStatus data={schedulesByStatus} typeFilter={typeFilter} month={month.value} year={year.value}/>
            <CustomersByGender data={customersByGender} typeFilter={typeFilter} month={month.value} year={year.value}/>
            <TopCustomers data={topCustomers} month={month.value} year={year.value}/>
          </>
        )}
      </DashboardContainer>
    </>
  )
}
